// src/components/landing/LandingIcons.jsx
//
// Icons for the landing page, drawn in the SAME frame as AnalysisTabIcons —
// imported, not copied, so the two sets cannot drift apart in stroke weight or
// sizing. The analysis icons are re-exported unchanged: a visitor sees the same
// subject-drawn marks a signed-in user meets on the analysis tabs.
//
// The same rule as the tab set: each one draws its subject. A price tag for
// cost, a balance for law and tradition, a set square for the architect — not
// one generic "feature" glyph repeated down the page.
import React from 'react';
import {
  IconFrame as S, IconDesign, IconCompliance, IconClimate, IconVastu,
} from '../AnalysisTabIcons.jsx';

export { IconDesign, IconCompliance, IconClimate, IconVastu };

/** Everyone at the table — three seats around one table. */
export const IconTable = () => (
  <S>
    <circle cx="12" cy="13.5" r="4" />
    <circle cx="12" cy="4.6" r="1.9" />
    <circle cx="4.3" cy="18.4" r="1.9" />
    <circle cx="19.7" cy="18.4" r="1.9" />
  </S>
);

/** Design to execution — a plan and a building, one line between them. */
export const IconFlow = () => (
  <S>
    <rect x="2.5" y="12" width="7" height="8.5" rx="1" />
    <path d="M9.5 16.2h4.5" />
    <path d="m12.3 14.3 1.9 1.9-1.9 1.9" />
    <path d="M15.5 20.5V8.5l3-2.5 3 2.5v12z" />
  </S>
);

/** Cost — a price tag. */
export const IconCost = () => (
  <S>
    <path d="M3 12.6V4.4A1.4 1.4 0 0 1 4.4 3h8.2l8.4 8.4-9.6 9.6z" />
    <circle cx="8" cy="8" r="1.6" />
  </S>
);

/** Know what's wrong — a lens over a fault mark. */
export const IconIssue = () => (
  <S>
    <circle cx="10.5" cy="10.5" r="6.5" />
    <path d="m15.4 15.4 5.6 5.6" />
    <path d="M10.5 7.4v3.6" />
    <path d="M10.5 13.6h.01" />
  </S>
);

/** Establish the site — a traced boundary with its surveyed corners. */
export const IconPlot = () => (
  <S>
    <path d="M4 6.5 15.5 4l4.5 8-4 8H5.5z" />
    <circle cx="4" cy="6.5" r="1.3" fill="currentColor" stroke="none" />
    <circle cx="15.5" cy="4" r="1.3" fill="currentColor" stroke="none" />
    <circle cx="20" cy="12" r="1.3" fill="currentColor" stroke="none" />
    <circle cx="16" cy="20" r="1.3" fill="currentColor" stroke="none" />
    <circle cx="5.5" cy="20" r="1.3" fill="currentColor" stroke="none" />
  </S>
);

/** Correct what was read — a room, with a corner handle being dragged. */
export const IconCorrect = () => (
  <S>
    <rect x="3" y="3" width="12" height="12" rx="1" />
    <path d="m15 15 6 6M21 16.5V21h-4.5" />
    <rect x="13.2" y="13.2" width="3.6" height="3.6" rx=".6" fill="currentColor" stroke="none" />
  </S>
);

/** Extrude, place and check — floor plates stacked into a massing. */
export const IconExtrude = () => (
  <S>
    <path d="M12 3 20 7.2 12 11.4 4 7.2z" />
    <path d="m4 11.4 8 4.2 8-4.2" />
    <path d="m4 15.6 8 4.2 8-4.2" />
  </S>
);

/** Law and tradition — a balance. */
export const IconScale = () => (
  <S>
    <path d="M12 3v18M7.5 21h9M5 7h14" />
    <path d="M5 7 2.5 13h5z" />
    <path d="m19 7-2.5 6h5z" />
  </S>
);

/** No AI does the arithmetic — a sum, worked in code. */
export const IconCalc = () => (
  <S>
    <rect x="4" y="2.5" width="16" height="19" rx="2" />
    <path d="M7.5 6.5h9" />
    <path d="M8 11h.01M12 11h.01M16 11h.01M8 14.5h.01M12 14.5h.01M8 18h.01M12 18h.01M16 14.5V18" />
  </S>
);

/** Nothing is metres until you say so — a scale bar with its ticks. */
export const IconRuler = () => (
  <S>
    <rect x="2.5" y="8" width="19" height="8" rx="1.2" />
    <path d="M6.5 8v3M10.5 8v4.5M14.5 8v3M18.5 8v4.5" />
  </S>
);

/** Honest about its limits — a gauge with its range drawn. */
export const IconLimits = () => (
  <S>
    <path d="M3.5 17a8.5 8.5 0 0 1 17 0" />
    <path d="m12 17 4-6" />
    <path d="M3.5 20.5h17" />
  </S>
);

/** Architect — a set square. */
export const IconArchitect = () => (
  <S>
    <path d="M4 20V4l16 16z" />
    <path d="M8 16v-4l4 4z" />
  </S>
);

/** Developer / builder — a building going up beside its crane. */
export const IconBuilder = () => (
  <S>
    <path d="M3 21h18" />
    <path d="M5 21V11h7v10" />
    <path d="M16 21V4M10 4h11M16 4l-4 4M20 4v4" />
  </S>
);

/** Homeowner — a house. */
export const IconHome = () => (
  <S>
    <path d="M3.5 11 12 4l8.5 7" />
    <path d="M6 9.5V20h12V9.5" />
    <path d="M10 20v-5h4v5" />
  </S>
);

/** Consultant — a structural I-section. */
export const IconBeam = () => (
  <S>
    <path d="M5 4h14M5 20h14M12 4v16" />
    <path d="M8.5 4v2.5M15.5 4v2.5M8.5 20v-2.5M15.5 20v-2.5" />
  </S>
);
